import { Button, Input, InputNumber, notification, Modal, Tooltip } from 'antd';
import { DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import type { Key } from 'react';
import {
  RiskRow,
  SelectOption,
  emptyFilledFilters
} from 'store/ducks/InternalControls/types';
import CustomSelect from 'components/CustomSelect';
import CustomDatePicker from 'components/CustomDatePicker';
import dayjs from 'dayjs';
import Helpers from 'utils/Helpers';
import {
  base_fields_risk,
  instance_fields_risk,
  riskCategoryOptions
} from './riskControlFieldsToDisable';
import ApiService from 'utils/ApiCalls';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import * as Styled from '../pages/InternalControlsMain/RiskControl.styled';

export const getRiskColumns = (
  risks: RiskRow[],
  probability_of_occurrence: SelectOption[],
  severity: SelectOption[],
  speed_of_occurrence: SelectOption[],
  risk_status: SelectOption[],
  update_cycle: SelectOption[],
  risk_trend: SelectOption[],
  setRisks: (risks: RiskRow[] | ((prev: RiskRow[]) => RiskRow[])) => void,
  t: (key: string) => string,
  newRiskIds?: Set<number>,
  hasMainOrganization?: boolean,
  onDelete?: (id: number) => void
): ColumnsType<RiskRow> => {
  const handleDuplicateRiskRow = (record: RiskRow) => {
    const newId = generateUniqueId();

    const index = risks.findIndex((r) => r.id === record.id);

    const duplicatedRow: RiskRow = {
      ...structuredClone(record),
      id: newId,
      isNew: true,
      isCopied: true
    };

    const newRisks = [...risks];
    newRisks.splice(index + 1, 0, duplicatedRow);

    setRisks(newRisks);

    if (newRiskIds) {
      newRiskIds.add(newId);
    }
  };

  const findRiskIndex = (record: RiskRow) =>
    risks.findIndex((r) => r.id === record.id);

  const shouldDisable = (record: RiskRow, fieldKey: string) => {
    if (record.notUsed) return true;

    if (record.isCopied) {
      if (record.instance_id === 0) {
        return instance_fields_risk.has(fieldKey);
      }
      return base_fields_risk.has(fieldKey);
    }

    if (record.isNew) {
      return instance_fields_risk.has(fieldKey);
    }

    if (hasMainOrganization) {
      return instance_fields_risk.has(fieldKey);
    }
    return base_fields_risk.has(fieldKey);
  };

  return [
    {
      title: t('ACTIONS'),
      key: 'actions',
      width: 120,
      fixed: 'left',
      render: (_, record) => (
        <>
          <Styled.CopyIcon
            onClick={() => handleDuplicateRiskRow(record)}
            icon={<CopyOutlined />}
            style={{ marginRight: 8 }}
          />

          <Button
            type='link'
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              if (newRiskIds?.has(record.id)) {
                setRisks(risks.filter((r) => r !== record));
                return;
              }
              onDelete?.(record.id);
            }}
          />
        </>
      )
    },
    {
      title: t('RISK_TITLE'),
      dataIndex: 'title',
      key: 'title',
      width: 180,
      fixed: 'left',
      ellipsis: false,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('title'),
      render: (value: string, record, index) => {
        if (record.notUsed) {
          return (
            <Tooltip
              title='Disabled due to a previous Risk/Control type change.'
              placement='topLeft'
              mouseEnterDelay={0.1}
              mouseLeaveDelay={0.1}
              arrow={false}
            >
              <div>{value || '—'}</div>
            </Tooltip>
          );
        }

        return (
          <>
            <Styled.GlobalTooltipStyles />
            <Tooltip
              title={value}
              placement='topLeft'
              mouseEnterDelay={0.1}
              mouseLeaveDelay={0.1}
              arrow={false}
            >
              <Styled.ResizableTextArea
                value={value}
                placeholder='Enter risk title'
                rows={2}
                disabled={shouldDisable(record, 'title')}
                onChange={(e) => {
                  const actualIndex = findRiskIndex(record);
                  const newRisks = [...risks];
                  newRisks[actualIndex]._pinned = true;
                  newRisks[actualIndex].title = e.target.value;
                  setRisks(newRisks);
                }}
              />
            </Tooltip>
          </>
        );
      }
    },
    {
      title: t('RISK_DESCRIPTION'),
      dataIndex: 'description',
      key: 'description',
      width: 400,
      fixed: 'left',
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('description'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              typeof value === 'string' && value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value || ''}
              placeholder='Enter risk description'
              rows={3}
              disabled={shouldDisable(record, 'description')}
              onChange={(e) => {
                const actualIndex = findRiskIndex(record);
                const newRisks = [...risks];
                newRisks[actualIndex]._pinned = true;
                newRisks[actualIndex].description = e.target.value;
                setRisks(newRisks);
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t('FIRST_TIME_NOTICED'),
      dataIndex: 'start_date',
      key: 'start_date',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('start_date'),
      render: (value: string | null, record, index) => (
        <CustomDatePicker
          value={value ? dayjs(value) : null}
          disabled={shouldDisable(record, 'start_date')}
          onChange={(date: dayjs.Dayjs | null) => {
            const actualIndex = findRiskIndex(record);
            const newRisks = [...risks];
            newRisks[actualIndex].start_date = date
              ? date.format('YYYY-MM-DD')
              : null;
            setRisks(newRisks);
          }}
        />
      )
    },
    {
      title: t('CAUSE_ROOT_CAUSE'),
      dataIndex: 'root_cause',
      key: 'root_cause',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('root_cause'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              typeof value === 'string' && value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value || ''}
              placeholder='Enter root cause'
              rows={2}
              disabled={shouldDisable(record, 'root_cause')}
              onChange={(e) => {
                const actualIndex = findRiskIndex(record);
                const newRisks = [...risks];
                newRisks[actualIndex]._pinned = true;
                newRisks[actualIndex].root_cause = e.target.value;
                setRisks(newRisks);
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t('EVENT'),
      dataIndex: 'event',
      key: 'event',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('event'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              typeof value === 'string' && value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value || ''}
              placeholder='Enter event'
              rows={2}
              disabled={shouldDisable(record, 'event')}
              onChange={(e) => {
                const actualIndex = findRiskIndex(record);
                const newRisks = [...risks];
                newRisks[actualIndex]._pinned = true;
                newRisks[actualIndex].event = e.target.value;
                setRisks(newRisks);
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t('IMPACT_CONSEQUENCE'),
      dataIndex: 'impact_consequence',
      key: 'impact_consequence',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('impact_consequence'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              typeof value === 'string' && value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value || ''}
              placeholder='Enter impact (consequence)'
              rows={2}
              disabled={shouldDisable(record, 'impact_consequence')}
              onChange={(e) => {
                const actualIndex = findRiskIndex(record);
                const newRisks = [...risks];
                newRisks[actualIndex]._pinned = true;
                newRisks[actualIndex].impact_consequence = e.target.value;
                setRisks(newRisks);
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t('RISK_CATEGORY'),
      dataIndex: 'category',
      key: 'category',
      width: 180,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('category'),
      render: (value: string | null, record, index) => (
        <CustomSelect
          value={value ?? undefined}
          options={riskCategoryOptions}
          placeholder='Select risk category'
          disabled={shouldDisable(record, 'category')}
          onChange={(selectedValue: string) => {
            const actualIndex = findRiskIndex(record);
            const newRisks = [...risks];
            newRisks[actualIndex]._pinned = true;
            newRisks[actualIndex].category = selectedValue;
            setRisks(newRisks);
          }}
        />
      )
    },
    {
      title: t('PROBABILITY_OF_OCCURRENCE'),
      dataIndex: 'probability_of_occurrence',
      key: 'probability_of_occurrence',
      width: 300,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>(
        'probability_of_occurrence'
      ),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={probability_of_occurrence}
          placeholder='Select probability of occurance'
          disabled={shouldDisable(record, 'probability_of_occurrence')}
          onChange={(id: number) => {
            const actualIndex = findRiskIndex(record);
            const selected = probability_of_occurrence.find(
              (o: { value: number }) => o.value === id
            );
            const newRisks = [...risks];

            newRisks[actualIndex]._pinned = true;
            newRisks[actualIndex].probability_of_occurrence = {
              id,
              name: selected?.label ?? null
            };
            setRisks(newRisks);
          }}
        />
      )
    },
    {
      title: t('SEVERITY_IMPACT_STRENGTH'),
      dataIndex: 'severity',
      key: 'severity',
      width: 300,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('severity'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={severity}
          placeholder='Select severity / Impact Strength'
          disabled={shouldDisable(record, 'severity')}
          onChange={(id: number) => {
            const actualIndex = findRiskIndex(record);
            const selected = severity.find(
              (o: { value: number }) => o.value === id
            );
            const newControls = [...risks];

            newControls[actualIndex]._pinned = true;
            newControls[actualIndex].severity = {
              id,
              name: selected?.label ?? null
            };
            setRisks(newControls);
          }}
        />
      )
    },
    {
      title: t('SPEED_OF_OCCURRENCE_VELOCITY'),
      dataIndex: 'speed_of_occurrence',
      key: 'speed_of_occurrence',
      width: 300,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('speed_of_occurrence'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={speed_of_occurrence}
          placeholder='Select speed of occurrence (velocity)'
          disabled={shouldDisable(record, 'speed_of_occurrence')}
          onChange={(id: number) => {
            const actualIndex = findRiskIndex(record);
            const selected = speed_of_occurrence.find(
              (o: { value: number }) => o.value === id
            );
            const newControls = [...risks];

            newControls[actualIndex]._pinned = true;
            newControls[actualIndex].speed_of_occurrence = {
              id,
              name: selected?.label ?? null
            };
            setRisks(newControls);
          }}
        />
      )
    },
    {
      title: t('RISK_ASSESSMENT_SCORE'),
      dataIndex: 'risk_assessment_score',
      key: 'risk_assessment_score',
      width: 250,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('risk_assessment_score'),
      render: (value: string, record, index) => (
        <Input
          value={value}
          type='number'
          placeholder='Enter risk score'
          disabled={shouldDisable(record, 'risk_assessment_score')}
          onChange={(e) => {
            const actualIndex = findRiskIndex(record);
            const newRisks = [...risks];
            newRisks[actualIndex]._pinned = true;
            newRisks[actualIndex].risk_assessment_score = e.target.value;
            setRisks(newRisks);
          }}
        />
      )
    },
    {
      title: t('RISK_TREND'),
      dataIndex: 'risk_trend',
      key: 'risk_trend',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('risk_trend'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={risk_trend}
          placeholder='Select risk trend'
          disabled={shouldDisable(record, 'risk_trend')}
          onChange={(id: number) => {
            const actualIndex = findRiskIndex(record);
            const selected = risk_trend.find(
              (o: { value: number }) => o.value === id
            );
            const newControls = [...risks];

            newControls[actualIndex]._pinned = true;
            newControls[actualIndex].risk_trend = {
              id,
              name: selected?.label ?? null
            };
            setRisks(newControls);
          }}
        />
      )
    },
    {
      title: t('INHERENT_RISK_EUR'),
      dataIndex: 'gross_risk',
      key: 'gross_risk',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('gross_risk'),
      render: (value: number | null, record, index) => (
        <InputNumber
          value={value}
          placeholder='Enter gross risk'
          min={0}
          step={1}
          precision={0}
          controls={false}
          disabled={shouldDisable(record, 'gross_risk')}
          onKeyPress={(e) => {
            if (!/[0-9]/.test(e.key)) {
              e.preventDefault();
            }
          }}
          onChange={(val) => {
            const actualIndex = findRiskIndex(record);
            const newRisks = [...risks];
            newRisks[actualIndex]._pinned = true;
            newRisks[actualIndex].gross_risk = val;
            setRisks(newRisks);
          }}
        />
      )
    },
    {
      title: t('RESIDUAL_RISK_EUR'),
      dataIndex: 'residual_risk',
      key: 'residual_risk',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('residual_risk'),
      render: (value: number | null, record, index) => (
        <InputNumber
          value={value}
          placeholder='Enter residual risk'
          min={0}
          step={1}
          precision={0}
          controls={false}
          disabled={shouldDisable(record, 'residual_risk')}
          onKeyPress={(e) => {
            if (!/[0-9]/.test(e.key)) {
              e.preventDefault();
            }
          }}
          onChange={(val) => {
            const actualIndex = findRiskIndex(record);
            const newRisks = [...risks];
            newRisks[actualIndex]._pinned = true;
            newRisks[actualIndex].residual_risk = val;
            setRisks(newRisks);
          }}
        />
      )
    },
    {
      title: t('RISK_STATUS'),
      dataIndex: 'risk_status',
      key: 'risk_status',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('risk_status'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={risk_status}
          placeholder='Select risk status'
          disabled={shouldDisable(record, 'risk_status')}
          onChange={(id: number) => {
            const actualIndex = findRiskIndex(record);
            const selected = risk_status.find(
              (o: { value: number }) => o.value === id
            );
            const newControls = [...risks];

            newControls[actualIndex]._pinned = true;
            newControls[actualIndex].risk_status = {
              id,
              name: selected?.label ?? null
            };
            setRisks(newControls);
          }}
        />
      )
    },
    {
      title: t('END_DATE'),
      dataIndex: 'end_date',
      key: 'end_date',
      width: 150,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('end_date'),
      render: (value: string | null, record, index) => (
        <CustomDatePicker
          value={value ? dayjs(value) : null}
          disabled={shouldDisable(record, 'end_date')}
          onChange={(date: dayjs.Dayjs | null) => {
            const actualIndex = findRiskIndex(record);
            const newRisks = [...risks];
            newRisks[actualIndex].end_date = date
              ? date.format('YYYY-MM-DD')
              : '9999-01-01';
            setRisks(newRisks);
          }}
        />
      )
    },
    {
      title: t('ASSESSMENT_DATE'),
      dataIndex: 'assessment_date',
      key: 'assessment_date',
      width: 150,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('assessment_date'),
      render: (value: string | null, record, index) => (
        <CustomDatePicker
          value={value ? dayjs(value) : null}
          disabled={shouldDisable(record, 'assessment_date')}
          onChange={(date: dayjs.Dayjs | null) => {
            const actualIndex = findRiskIndex(record);
            const newRisks = [...risks];
            newRisks[actualIndex].assessment_date = date
              ? date.format('YYYY-MM-DD')
              : null;
            setRisks(newRisks);
          }}
        />
      )
    },
    {
      title: t('UPDATE_CYCLE'),
      dataIndex: 'update_cycle',
      key: 'update_cycle',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('update_cycle'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={update_cycle}
          placeholder='Select update cycle'
          disabled={shouldDisable(record, 'update_cycle')}
          onChange={(id: number) => {
            const actualIndex = findRiskIndex(record);
            const selected = update_cycle.find(
              (o: { value: number }) => o.value === id
            );
            const newControls = [...risks];

            newControls[actualIndex]._pinned = true;
            newControls[actualIndex].update_cycle = {
              id,
              name: selected?.label ?? null
            };
            setRisks(newControls);
          }}
        />
      )
    },
    {
      title: t('OWNER_MAINTAINER'),
      dataIndex: 'owner_maintainer',
      key: 'owner_maintainer',
      width: 180,
      ellipsis: false,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('owner_maintainer'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={value || ''}
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value || ''}
              placeholder='Enter owner / maintainer'
              rows={2}
              disabled={shouldDisable(record, 'owner_maintainer')}
              onChange={(e) => {
                const actualIndex = findRiskIndex(record);
                const newRisks = [...risks];
                newRisks[actualIndex]._pinned = true;
                newRisks[actualIndex].owner_maintainer = e.target.value;
                setRisks(newRisks);
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t('RISK_OWNER'),
      dataIndex: 'risk_owner',
      key: 'risk_owner',
      width: 180,
      ellipsis: false,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('risk_owner'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={value || ''}
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value || ''}
              placeholder='Enter risk owner'
              rows={2}
              disabled={shouldDisable(record, 'risk_owner')}
              onChange={(e) => {
                const actualIndex = findRiskIndex(record);
                const newRisks = [...risks];
                newRisks[actualIndex]._pinned = true;
                newRisks[actualIndex].risk_owner = e.target.value;
                setRisks(newRisks);
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t('KEY_RISK_INDICATORS_KRI'),
      dataIndex: 'key_risk_indicators',
      key: 'key_risk_indicators',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('key_risk_indicators'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={value || ''}
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value || ''}
              placeholder='Enter key risk Indicators'
              rows={2}
              disabled={shouldDisable(record, 'key_risk_indicators')}
              onChange={(e) => {
                const actualIndex = findRiskIndex(record);
                const newRisks = [...risks];
                newRisks[actualIndex]._pinned = true;
                newRisks[actualIndex].key_risk_indicators = e.target.value;
                setRisks(newRisks);
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t('CREATED_AT'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('created_at'),
      render: (value: string | null, record, index) => (
        <CustomDatePicker
          value={value ? dayjs(value) : null}
          disabled={shouldDisable(record, 'created_at')}
          onChange={(date: dayjs.Dayjs | null) => {
            const actualIndex = findRiskIndex(record);
            const newRisks = [...risks];
            newRisks[actualIndex].created_at = date
              ? date.format('YYYY-MM-DD')
              : null;
            setRisks(newRisks);
          }}
        />
      )
    },
    {
      title: t('UPDATED_AT'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<RiskRow>('updated_at'),
      render: (value: string | null, record, index) => (
        <CustomDatePicker
          value={value ? dayjs(value) : null}
          disabled={shouldDisable(record, 'updated_at')}
          onChange={(date: dayjs.Dayjs | null) => {
            const actualIndex = findRiskIndex(record);
            const newRisks = [...risks];
            newRisks[actualIndex].updated_at = date
              ? date.format('YYYY-MM-DD')
              : null;
            setRisks(newRisks);
          }}
        />
      )
    },
    {
      title: t('Not used'),
      dataIndex: 'notUsed',
      key: 'notUsed',
      width: 120,
      ellipsis: true,
      filters: [
        { text: 'true', value: true },
        { text: 'false', value: false }
      ],
      onFilter: (value: boolean | Key, record: RiskRow) =>
        record.notUsed === (value as unknown as boolean),
      render: (value: boolean) => <span>{String(value)}</span>
    }
    // {
    //   title: t('ACTIONS'),
    //   key: 'actions',
    //   width: 180,
    //   render: (_, record) => (
    //     <>
    //       <Button
    //         type='link'
    //         danger
    //         icon={<DeleteOutlined />}
    //         onClick={() => {
    //           Modal.confirm({
    //             title: 'Are you sure?',
    //             content:
    //               'Do you want to delete this risk? This action cannot be undone.',
    //             okText: 'Yes, Delete',
    //             cancelText: 'Cancel',
    //             okType: 'danger',
    //             onOk: async () => {
    //               if (newRiskIds?.has(record.id)) {
    //                 const newRisks = risks.filter((r) => r !== record);
    //                 setRisks(newRisks);
    //                 return;
    //               }
    //               try {
    //                 await ApiService.softDeleteRiskMaintenance(record.id);
    //                 notification.success({
    //                   message: 'Risk Deleted',
    //                   description: 'Risk has been soft deleted successfully.',
    //                   placement: 'topRight',
    //                   duration: 3
    //                 });
    //                 const newRisks = risks.filter((r) => r !== record);
    //                 setRisks(newRisks);
    //               } catch (error) {
    //                 notification.error({
    //                   message: 'Delete Failed',
    //                   description: 'Failed to delete risk. Please try again.',
    //                   placement: 'topRight',
    //                   duration: 3
    //                 });
    //               }
    //             }
    //           });
    //         }}
    //       />
    //     </>
    //   )
    // }
  ];
};
